import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CarouselBackdrop from 'components/shared/carousels/CarouselBackdrop';
import routes from 'common/routes';

const MAX_ITEMS = 5;

const clamp2 = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden'
};

// Descripción breve: usa el resumen; si viene vacío, un excerpt del cuerpo (sin HTML).
const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, ' ').replace(/&[a-z]+;/gi, ' ').replace(/\s+/g, ' ').trim();
const getDescription = (item) => {
  if (item.summary) return item.summary;
  const text = stripHtml(item.body);
  return text.length > 90 ? `${text.slice(0, 90).trim()}…` : text;
};

/**
 * Lista compacta de noticias para el inicio en MOBILE. Reutiliza las noticias ya cargadas en
 * el store por ClientDashboard (newsHubFields).
 *
 * `skipFeatured`: la primera noticia ya se muestra arriba en el carrusel destacado, así que
 * aquí se omite y la lista arranca en la segunda (sin repetir contenido).
 */
function MobileNewsList({ skipFeatured = false }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['news', 'routes']);
  const news = useSelector((state) => state.news.data.news) || [];

  const items = skipFeatured ? news.slice(1, MAX_ITEMS) : news.slice(1, MAX_ITEMS);
  const showHero = !skipFeatured && Boolean(news[0]);

  if (news.length === 0 || (skipFeatured && items.length === 0)) return null;

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString(i18n.language === 'es' ? 'es-CO' : 'en-US', { day: '2-digit', month: 'short' });

  const openArticle = (id) => navigate(routes.viewNews.path.replace(':id', id));

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography component="h2" sx={{ fontWeight: 800, fontSize: '1.15rem' }}>
          {skipFeatured
            ? t('moreNews', { ns: 'news', defaultValue: 'Más noticias' })
            : t('news.list', { ns: 'routes', defaultValue: 'Noticias' })}
        </Typography>
        <Typography
          role="button"
          tabIndex={0}
          onClick={() => navigate(routes.listNews.path)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(routes.listNews.path); } }}
          variant="body2"
          sx={{ color: 'gold.main', fontWeight: 700, cursor: 'pointer' }}>
          {t('seeAll', { ns: 'news', defaultValue: 'Ver todas' })}
        </Typography>
      </Stack>

      {/* Primera noticia como card destacada con el mismo lenguaje del carrusel desktop:
          superficie oscura en ambos modos con la imagen difuminada de fondo. */}
      {showHero && (
        <Box
          onClick={() => openArticle(news[0].id)}
          role="button"
          aria-label={news[0].title}
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openArticle(news[0].id); } }}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '16px',
            border: '1px solid',
            borderColor: 'hairline.main',
            backgroundColor: '#101214',
            boxShadow: 'var(--otx-card-shadow)',
            minHeight: 180,
            p: 2,
            mb: 1.5,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            cursor: 'pointer',
            '&:active': { transform: 'scale(0.98)' },
            transition: 'transform 200ms cubic-bezier(0.32,0.72,0,1)'
          }}>
          <CarouselBackdrop image={news[0].featured_image || news[0].thumbnail} alt="" />
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="caption" sx={{ color: 'gold.bright', fontWeight: 700 }}>
              {formatDate(news[0].created_at)}
            </Typography>
            <Typography sx={{ color: '#FFFFFF', fontWeight: 800, lineHeight: 1.25, fontSize: '1.05rem', ...clamp2 }}>
              {news[0].title}
            </Typography>
            {getDescription(news[0]) && (
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.72)', ...clamp2, display: '-webkit-box' }}>
                {getDescription(news[0])}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <Stack spacing={1.5}>
        {items.map((item) => (
          <Box
            key={item.id}
            onClick={() => openArticle(item.id)}
            role="button"
            aria-label={item.title}
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openArticle(item.id); } }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1,
              cursor: 'pointer',
              borderRadius: '14px',
              border: '1px solid',
              borderColor: 'hairline.main',
              backgroundColor: 'surface.content',
              transition: 'background-color 200ms cubic-bezier(0.32,0.72,0,1), transform 200ms cubic-bezier(0.32,0.72,0,1)',
              '&:hover': { backgroundColor: 'surface.hover' },
              '&:active': { transform: 'scale(0.98)' }
            }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '10px', flexShrink: 0, overflow: 'hidden', backgroundColor: 'surface.raised' }}>
              {(item.featured_image || item.thumbnail) && (
                <Box
                  component="img"
                  src={item.featured_image || item.thumbnail}
                  alt={item.title}
                  sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              )}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontWeight: 700, lineHeight: 1.3, fontSize: '0.95rem', ...clamp2 }}>
                {item.title}
              </Typography>
              {getDescription(item) && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {getDescription(item)}
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mt: 0.25 }}>
                {formatDate(item.created_at)}
              </Typography>
            </Box>
            <ChevronRightIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

MobileNewsList.propTypes = {
  skipFeatured: PropTypes.bool
};

export default MobileNewsList;
